import { Link, useLocation, useMatchRoute } from '@tanstack/react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { Button } from '@workspace/ui/components/button'
import { cn, getInitials } from '@workspace/ui/lib/utils'
import { Mic, Wallet } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { MobileNav } from '@/components/mobile-nav'
import { adminNavItems, investorTabs, startupTabs } from '@/components/nav-items'
import { NavUser } from '@/components/nav-user'
import { WelcomeModal } from '@/components/welcome-modal'
import { TermsDialog } from '@/features/legal/components/terms-dialog'
import {
  useInvestorCreditBalanceQuery,
  useInvestorQuery,
  useStartupCreditBalanceQuery,
  useStartupQuery,
} from '@/hooks/api'
import { useIsFounder, useIsInvestor } from '@/hooks/use-user-type'
import { useSession } from '@/lib/auth'

export function AppLayout({ children }: { children: ReactNode }) {
  const [termsOpen, setTermsOpen] = useState(false)
  const { data: session } = useSession()
  const isFounder = useIsFounder()
  const isInvestor = useIsInvestor()
  const { data: startupData } = useStartupQuery({ enabled: isFounder })
  const { data: investorData } = useInvestorQuery({ enabled: isInvestor })
  const user = session?.user
  const startup = startupData?.data
  const investor = investorData?.data

  const startupCreditBalanceQuery = useStartupCreditBalanceQuery(startup?.id)
  const investorCreditBalanceQuery = useInvestorCreditBalanceQuery(investor?.id)
  const creditBalance =
    startupCreditBalanceQuery.data?.data.balance ?? investorCreditBalanceQuery.data?.data.balance

  const hasOrg = !!startup || !!investor
  const matchRoute = useMatchRoute()
  const pathname = useLocation({ select: (l) => l.pathname })
  const isAdminRoute = pathname.startsWith('/admin')
  const isCreditsActive = matchRoute({ to: '/credits', fuzzy: false })

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-50 w-full">
        <div className="absolute inset-0 bg-linear-to-b from-background to-transparent backdrop-blur-md pointer-events-none" />
        <div className="relative container max-w-6xl mx-auto flex h-16 items-center justify-between px-4">
          {/* Mobile header */}
          <div className="flex items-center gap-2 md:hidden">
            <Link to="/" className="flex items-center">
              <img src="/seedforge-logo.svg" alt="SeedForge" className="h-10" />
            </Link>
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <MobileNav
              startup={startup}
              investor={investor}
              user={
                user
                  ? {
                      name: user.name || user.email || 'User',
                      email: user.email || '',
                      avatar: user.image || '',
                    }
                  : null
              }
              creditBalance={creditBalance}
              isAdminRoute={isAdminRoute}
            />
          </div>

          {/* Desktop header */}
          <div className="hidden md:flex items-center">
            <Link to="/" className="flex items-center">
              <img src="/seedforge-logo.svg" alt="SeedForge" className="h-10" />
            </Link>
            {isAdminRoute ? (
              <AdminNav />
            ) : (
              <>
                {startup && <StartupNav startup={startup} />}
                {investor && <InvestorNav investor={investor} />}
              </>
            )}
          </div>
          <div className="hidden md:flex items-center gap-2">
            {!isAdminRoute && startup && (
              <Button size="sm" asChild>
                <Link to="/pitches/new">
                  <Mic className="size-3.5" />
                  Pitch Now
                </Link>
              </Button>
            )}
            {!isAdminRoute && startup && hasOrg && (
              <div className="h-6 w-px bg-linear-to-b from-transparent via-[#f97316]/40 to-transparent" />
            )}
            {!isAdminRoute && hasOrg && (
              <Link
                to="/credits"
                className={cn(
                  'flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-all',
                  isCreditsActive ? 'text-[#f97316]' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Wallet className="size-3.5" />
                {creditBalance !== undefined ? creditBalance : '–'}
              </Link>
            )}
            {!isAdminRoute && hasOrg && (
              <div className="h-6 w-px bg-linear-to-b from-transparent via-[#f97316]/40 to-transparent" />
            )}
            {user && (
              <NavUser
                user={{
                  name: user.name || user.email || 'User',
                  email: user.email || '',
                  avatar: user.image || '',
                }}
              />
            )}
          </div>
        </div>
      </header>
      <main className="container max-w-6xl mx-auto flex flex-1 flex-col gap-4 px-4 pb-16">
        {children}
      </main>
      <footer className="border-t border-border/40 py-6">
        <div className="container max-w-6xl mx-auto px-4 text-center text-xs text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} SeedForge. All rights reserved.</p>
          <button
            type="button"
            onClick={() => setTermsOpen(true)}
            className="mt-1 hover:text-foreground transition-colors underline-offset-4 hover:underline"
          >
            Terms & Conditions
          </button>
        </div>
      </footer>
      <WelcomeModal />
      <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} />
    </div>
  )
}

function StartupNav({
  startup,
}: {
  startup: { id: string; name: string; logoUrl?: string | null }
}) {
  const matchRoute = useMatchRoute()

  return (
    <div className="flex items-center">
      <div className="mx-2 h-6 w-px bg-linear-to-b from-transparent via-[#f97316]/40 to-transparent" />
      <Link
        to="/"
        className={cn(
          'flex items-center gap-2 rounded-md p-1.5 text-sm transition-colors',
          matchRoute({ to: '/', fuzzy: false })
            ? 'text-[#f97316]'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <Avatar className="size-7 rounded-full">
          {startup.logoUrl && <AvatarImage src={startup.logoUrl} alt={startup.name} />}
          <AvatarFallback className="rounded-full bg-elevated text-white text-xs font-medium">
            {getInitials(startup.name)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate font-medium max-w-32">{startup.name}</span>
      </Link>
      <div className="mx-2 h-6 w-px bg-linear-to-b from-transparent via-[#f97316]/40 to-transparent" />
      <nav className="flex items-center">
        {startupTabs.map((tab) => {
          const isActive = matchRoute({ to: tab.to, fuzzy: false })
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                'px-2.5 py-1 text-sm font-medium rounded-md transition-colors',
                isActive ? 'text-[#f97316]' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}

function AdminNav() {
  const pathname = useLocation({ select: (l) => l.pathname })

  return (
    <div className="flex items-center">
      <div className="mx-2 h-6 w-px bg-linear-to-b from-transparent via-[#f97316]/40 to-transparent" />
      <nav className="flex items-center">
        {adminNavItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              'px-2.5 py-1 text-sm font-medium rounded-md transition-colors',
              pathname.startsWith(item.to)
                ? 'text-[#f97316]'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {item.label}
          </Link>
        ))}
      </nav>
    </div>
  )
}

function InvestorNav({ investor }: { investor: { name: string; logoUrl?: string | null } }) {
  const matchRoute = useMatchRoute()

  return (
    <div className="flex items-center">
      <div className="mx-2 h-6 w-px bg-linear-to-b from-transparent via-[#f97316]/40 to-transparent" />
      <Link
        to="/"
        className={cn(
          'flex items-center gap-2 rounded-md p-1.5 text-sm transition-colors',
          matchRoute({ to: '/', fuzzy: false })
            ? 'text-[#f97316]'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <Avatar className="size-7 rounded-full">
          {investor.logoUrl && <AvatarImage src={investor.logoUrl} alt={investor.name} />}
          <AvatarFallback className="rounded-full bg-elevated text-white text-xs font-medium">
            {getInitials(investor.name)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate font-medium max-w-32">{investor.name}</span>
      </Link>
      <div className="mx-2 h-6 w-px bg-linear-to-b from-transparent via-[#f97316]/40 to-transparent" />
      <nav className="flex items-center">
        {investorTabs.map((tab) => {
          const isActive = matchRoute({ to: tab.to, fuzzy: false })
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                'px-2.5 py-1 text-sm font-medium rounded-md transition-colors',
                isActive ? 'text-[#f97316]' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}
